import ViatelLogo from '@/components/common/ViatelLogo';
import { canChangeCompany } from '@/types/user';
import { changeCompanyRedirect } from '@/util/auth';
import { useConfig } from '@/hooks/useConfig';
import { setOpacity } from '@/util/color';
import PersonIcon from '@mui/icons-material/Person';
import { Avatar, Box, Divider, IconButton, ListItemText, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import React, { FC, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useLocation } from 'react-router';

type UserAvatarProps = Record<string, never>;

const UserAvatar: FC<UserAvatarProps> = () => {
  const theme = useTheme();
  const auth = useAuth();
  const { config } = useConfig();
  const { pathname, search } = useLocation();
  const showChangeCompany = canChangeCompany(auth.user?.profile);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogClick = () => {
    sessionStorage.setItem('authRedirectUrl', `${pathname}${search}`);
    if (auth.isAuthenticated) {
      auth.removeUser();
      auth.signoutRedirect({
        id_token_hint: auth.user?.id_token,
        post_logout_redirect_uri: window.location.origin
      });
    } else {
      auth.signinRedirect();
    }
    handleCloseUserMenu();
  };

  const handleChangeCompanyClick = () => {
    sessionStorage.setItem('authRedirectUrl', `${pathname}${search}`);
    changeCompanyRedirect(config, auth);
  };

  const getInitials = (): string => {
    return `${auth.user?.profile.given_name?.charAt(0)}${auth.user?.profile.family_name?.charAt(0)}`;
  };

  return (
    <Box sx={{ flexGrow: 0 }}>
      <IconButton size='small' onClick={handleOpenUserMenu} sx={{ p: 0 }}>
        <Avatar
          alt={(auth.user && auth.user.profile.family_name) || '?'}
          sx={{
            bgcolor: setOpacity(theme.palette.primary.main, 0.5),
            color: theme.palette.common.white,
            height: theme.spacing(4),
            width: theme.spacing(4)
          }}
        >
          {auth.isAuthenticated ? getInitials() : <PersonIcon />}
        </Avatar>
      </IconButton>
      <Menu
        sx={{ mt: '45px', maxWidth: '20rem' }}
        id='menu-appbar'
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
      >
        <MenuItem
          sx={
            {
              // display: {
              //   sm: 'block',
              //   md: 'none',
              // },
            }
          }
        >
          <ViatelLogo white={theme.palette.mode === 'dark'} />
        </MenuItem>
        {auth.isAuthenticated && (
          <MenuItem>
            <ListItemText
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'start'
              }}
            >
              <Typography textAlign='left' variant='body1'>
                {`${auth.user?.profile.company_name || ''}`}
              </Typography>
              <Typography textAlign='left' variant='body2' sx={{ pt: 1 }}>
                {`${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`}
              </Typography>
            </ListItemText>
          </MenuItem>
        )}
        {showChangeCompany && <Divider />}
        {showChangeCompany && (
          <MenuItem onClick={handleChangeCompanyClick}>
            <ListItemText>Change Company</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleLogClick}>
          <Typography textAlign='center'>{auth.isAuthenticated ? 'Logout' : 'Login'}</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default UserAvatar;
